<template>
  <div>
    <div class="achievement-header">
      <span class="achievement-icon">{{
        Array.isArray(achievement?.icon) ? achievement?.icon[0] : (achievement?.icon ?? '')
      }}</span>
      <span class="achievement-title">{{ achievement ? achievement.title : '' }}</span>
      <span class="achievement-stars">
        <span v-if="achievement">
          <span v-for="i in achievement.stars" :key="i" class="star">⭐️</span>
        </span>
      </span>
    </div>

    <div class="achievement-grid" v-if="achievement?.available">
      <div
        class="infinite-pill toggle-mode"
        v-if="achievement.percentageInfinite === undefined"
        :class="{ 'dark-mode': !hasMadeGuessInInfiniteMode }"
      >
        <span class="infinite-symbol">♾️</span>{{ hasMadeGuessInInfiniteMode ? 'On' : 'Off' }}
      </div>
      <div
        class="infinite-pill toggle-mode"
        v-else
        :class="{ 'dark-mode': achievement.percentageInfinite == 0 }"
      >
        <span class="infinite-symbol">♾️</span>
        <span v-if="achievement.percentageInfinite === 0">Off</span>
        <span v-if="achievement.percentageInfinite > 0"
          >On {{ Math.round(achievement.percentageInfinite * 1000) / 10 }}%</span
        >
      </div>
      <div class="cell" v-for="(cell, index) in cellIcons" :key="index">
        <span v-html="cell"></span>
      </div>
    </div>

    <div class="achievement-locked" v-if="achievement && !achievement?.available">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="45"
        height="45"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    </div>

    <div class="achievement-stats" v-if="achievement?.available">
      <p class="achievement-description">
        {{ achievement.description }}
      </p>
      <p class="achievement-score">
        Score: <span>{{ score }}</span> | Rank: <span>{{ formatNumber(rank) }}</span> of
        {{ formatNumber(plays) }}
      </p>
    </div>
    <div class="achievement-stats" v-if="achievement && !achievement.available">
      <p class="achievement-description">
        {{ achievement.descriptionLong ?? achievement.description }}
      </p>
      <div v-if="achievement.percentageComplete" class="progress-container">
        <div
          class="progress-bar"
          :style="{ width: `${Math.min(achievement.percentageComplete * 100, 100)}%` }"
        ></div>
      </div>
    </div>

    <button class="btn-primary" v-if="achievement?.available" @click="shareAchievement">
      <span class="material-icons-round"> share </span>
      <span class="desktop-only">{{
        copySuccessful ? 'Copied to clipboard!' : 'Share This Achievement'
      }}</span>
      <span class="mobile-only">{{ copySuccessful ? 'Copied!' : 'Share' }}</span>
    </button>

    <button
      class="share-button"
      v-if="achievement && !achievement.available"
      :class="{ locked: !achievement.available }"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
      <p>Unlock to Share</p>
    </button>
  </div>
</template>

<script>
  export default {
    name: 'AchievementDisplay',
    props: [
      'achievement',
      'position',
      'numMoreStars',
      'numLessStars',
      'numSameStars',
      'score',
      'rank',
      'plays',
      'hasMadeGuessInInfiniteMode',
      'coordsAndRarities',
      'boardId',
    ],
    data() {
      return {
        copySuccessful: false,
      };
    },
    computed: {
      formatNumber() {
        return (num) => new Intl.NumberFormat().format(num);
      },
      beforeIcon() {
        if (!this.achievement) return '';
        if (!this.achievement.icon) return '';
        if (Array.isArray(this.achievement.icon)) {
          return this.achievement.icon[0];
        }
        return '';
      },
      afterIcon() {
        if (!this.achievement) return '';
        if (!this.achievement.icon) return '';
        if (Array.isArray(this.achievement.icon)) {
          return this.achievement.icon[1];
        }
        return this.achievement.icon;
      },
      cellIcons() {
        const cells = [];
        for (let i = 0; i < 3; i++) {
          for (let j = 0; j < 3; j++) {
            const car = this.coordsAndRarities.find((cell) => {
              return cell[0] === i && cell[1] === j;
            });
            if (car) {
              let keyCellIndex = -1;
              if (this.achievement && this.achievement.keyCells) {
                keyCellIndex = this.achievement.keyCells.findIndex(
                  (cell) => cell[0] === i && cell[1] === j
                );
              }
              if (keyCellIndex > -1 && this.achievement.icon) {
                if (Array.isArray(this.achievement.icon)) {
                  cells.push(this.achievement.icon[keyCellIndex % this.achievement.icon.length]);
                } else {
                  cells.push(this.achievement.icon);
                }
              } else {
                cells.push('🟩');
              }
            } else {
              cells.push('❌');
            }
          }
        }
        return cells;
      },
    },
    methods: {
      resetShareButtonState() {
        this.copySuccessful = false;
      },
      shareAchievement() {
        if (!this.achievement) return;
        if (!this.achievement.available) return;

        // Add sharing functionality here
        let cells = '';
        for (let i = 0; i < this.cellIcons.length; i++) {
          cells += this.cellIcons[i];
          if (i == 2 || i == 5) {
            cells += '\n';
          }
        }

        let starsSuffix = '';
        if (this.achievement.stars > 0) {
          starsSuffix += ' | ';
          for (let i = 0; i < this.achievement.stars; i++) {
            starsSuffix += '★';
          }
        }

        let infiniteModeText = '';
        if (this.achievement.percentageInfinite === undefined) {
          infiniteModeText = `${this.hasMadeGuessInInfiniteMode ? 'On' : 'Off'}`;
        } else if (this.achievement.percentageInfinite === 0) {
          infiniteModeText = 'Off';
        } else {
          infiniteModeText = `On for ${Math.round(this.achievement.percentageInfinite * 1000) / 10}% of games`;
        }

        let shareTextLines = [
          cells,
          `Score: ${this.score} | Rank: ${this.formatNumber(this.rank)}/${this.formatNumber(this.plays)}`,
          `${this.beforeIcon ? `${this.beforeIcon} ` : ''}${this.achievement.title} ${this.afterIcon}${starsSuffix}`,
          `${this.achievement.descriptionLong ?? this.achievement.description}`,
          `Board #${this.boardId} | ♾️ Mode: ${infiniteModeText}`,
          `https://geogridgame.com`,
        ];

        // Copy to clipboard
        navigator.clipboard.writeText(shareTextLines.join('\n')).then(
          () => {
            console.log('Copied to clipboard');
            this.copySuccessful = true;
          },
          (err) => {
            console.error('Could not copy text: ', err);
            this.copySuccessful = false;
          }
        );

        // Send event to Google Analytics
        if (window.gtag) {
          window.gtag('event', 'share_achievement', {
            title: this.achievement.title,
            stars: this.achievement.stars,
            position: this.position,
            numMoreStars: this.numMoreStars,
            numLessStars: this.numLessStars,
            numSameStars: this.numSameStars,
          });
        }
      },
    },
    mounted() {
      if (this.achievement?.cells) {
        this.cellIcons = this.achievement.cells.map((cell) => cell.icon);
      }
    },
  };
</script>

<style scoped>
  .btn-primary {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
    height: 44px;
    width: 50%;
    align-self: center;
    cursor: pointer;
    background-color: rgba(147, 147, 236, 1);
    color: rgba(26, 26, 26, 1);
    border: 1px rgba(147, 147, 236, 1);
    padding: 6px 12px;
    border-radius: 12px;
    transition: background-color 0.3s;
    font-size: 16px;
    font-family: 'Oxygen', Helvetica, Arial, sans-serif;
    font-weight: 700;
    box-sizing: border-box;
  }

  .share-button.locked {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
    cursor: not-allowed;
    height: 44px;
    max-width: 100%;
    background-color: transparent;
    color: rgba(147, 147, 236, 1);
    border: 2px rgba(147, 147, 236, 1) solid;
    padding: 6px 12px;
    border-radius: 12px;
    transition: background-color 0.3s;
    font-size: 14px;
    font-family: 'Oxygen', Helvetica, Arial, sans-serif;
    font-weight: 700;
    box-sizing: border-box;
  }

  .share-button:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .share-button svg {
    margin-right: 10px;
  }

  .share-icon {
    flex-shrink: 0;
  }

  .achievement-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px;
  }

  .achievement-icon {
    font-size: 20px;
  }

  .achievement-title {
    font-weight: bold;
    font-size: 18px;
    margin: 0 10px;
  }

  .achievement-stars {
    color: #ffc107;
  }

  .achievement-grid {
    --cell-size: 40px;
    position: relative;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: calc(var(--cell-size) * 3);
    height: calc(var(--cell-size) * 3);
    margin: auto;
    background-color: #131722;
    border-radius: 5px;
  }

  .achievement-grid .infinite-pill {
    position: absolute;
    left: 100%;
    margin-left: 12px;
    font-size: smaller;
  }

  .achievement-grid .cell {
    height: var(--cell-size);
    line-height: var(--cell-size);
  }

  .toggle-mode {
    background-color: rgba(147, 147, 236, 1);
    color: #1a1e2e;
    padding: 5px 15px;
    border-radius: 40px;
    font-weight: bold;
    display: flex;
    align-items: center;
  }

  .toggle-mode.dark-mode {
    background-color: #1a1e2e;
    color: #cccccc;
  }

  .infinite-symbol {
    margin-right: 5px;
  }

  .grid-row {
    display: flex;
    justify-content: space-around;
    margin-bottom: 10px;
  }

  .grid-cell {
    width: 30%;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    border-radius: 5px;
  }

  .achievement-stats {
    text-align: center;
    margin-bottom: 20px;
  }

  .achievement-description {
    font-size: 16px;
    margin-bottom: 5px;
  }

  .achievement-score {
    color: #8d8f97;
  }

  .achievement-score span {
    color: rgba(147, 147, 236, 1);
  }

  .achievement-locked {
    color: rgba(147, 147, 236, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    background-color: #2a2a3a;
    border-radius: 100%;
    width: 80px;
    height: 80px;
  }

  .progress-container {
    width: 80%;
    max-width: 600px;
    height: 10px;
    background-color: #1a1e2e;
    border-radius: 5px;
    margin: 20px auto;
    overflow: hidden;
  }

  .progress-bar {
    height: 100%;
    background-color: rgba(147, 147, 236, 1);
    border-radius: 5px;
    transition: width 0.3s ease;
  }

  .mobile-only {
    display: none;
  }

  @media (max-width: 768px), (max-height: 980px) {
    .mobile-only {
      display: block;
    }
    .desktop-only {
      display: none;
    }
  }

  @media (max-height: 693px) {
    .achievement-grid {
      --cell-size: 36px;
    }
    .achievement-grid .cell {
      font-size: 13px;
    }
    .achievement-score,
    .achievement-description {
      font-size: small;
    }
  }
</style>
